import type {
  CabinetBranchAccess,
  CabinetMemberView,
  CabinetRoleOption,
} from "@planerius/contracts/cabinet-auth"
import { type ReactNode, useState } from "react"

import { listRoles } from "../../../api/invitations.api"
import {
  listMemberSessions,
  restoreMember,
  revokeMember,
  suspendMember,
  updateMemberAccess,
} from "../../../api/members.api"
import { type ApiProblem, isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { Badge, Button, Checkbox, SelectField } from "../../../ui"
import { useResource } from "../use-resource"
import {
  REVOKED_TEXT,
  branchesWords,
  denialOf,
  isConnectionProblem,
  mfaWords,
  removedWords,
  roleWords,
  sessionsWords,
  statusBadge,
} from "./member-model"
import { roleOptionLabel } from "./invite-dialog-model"
import styles from "./member-card.module.css"

/**
 * 4-Б10 «Сотрудник · отзыв доступа» (макет П2, ТЗ 04, Б10).
 *
 * ГЛАВНОЕ ДЕЙСТВИЕ — «ЗАКРЫТЬ ДОСТУП», И ЧЕЛОВЕК ДОЛЖЕН ТОЧНО ЗНАТЬ, СРАБОТАЛО ЛИ ОНО.
 * Поэтому итог — отдельным текстом, а обрыв связи — своим текстом с «доступ НЕ закрыт»:
 * недосказанность здесь опаснее всего. Повтор безопасен — сервер считает повторное
 * закрытие успехом. Окно, а не отдельная страница: отзыв обязан работать с телефона,
 * и окно журнала на узком экране занимает его целиком.
 */
export function MemberCard(props: {
  readonly member: CabinetMemberView
  readonly branches: readonly CabinetBranchAccess[]
  readonly viewerUserId: string
  readonly canManage: boolean
  readonly timeZone: string
  readonly onChanged: (member: CabinetMemberView) => void
  readonly onClose: () => void
}): ReactNode {
  const [member, setMember] = useState(props.member)
  const [confirm, setConfirm] = useState<"revoke" | "suspend" | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<{ readonly tone: "ok" | "error"; readonly text: ReactNode } | null>(null)

  const removed = member.status === "removed"
  const sessions = useResource(
    removed || !props.canManage ? null : (signal) => listMemberSessions(member.memberId, signal),
    removed || !props.canManage ? null : `member-sessions-${member.memberId}`,
  )
  const denied = denialOf(member, props.viewerUserId, props.canManage) ?? undefined
  const badge = statusBadge(member)

  const changed = (next: CabinetMemberView): void => {
    setMember(next)
    props.onChanged(next)
  }

  const run = async (
    action: () => Promise<CabinetMemberView>,
    success: string,
    failure: (problem: ApiProblem) => ReactNode,
  ): Promise<void> => {
    setBusy(true)
    setOutcome(null)
    try {
      changed(await action())
      setOutcome({ tone: "ok", text: success })
      setConfirm(null)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setOutcome({ tone: "error", text: failure(error) })
    } finally {
      setBusy(false)
    }
  }

  const revoke = (): Promise<void> =>
    run(
      () =>
        revokeMember({ memberId: member.memberId, expectedUpdatedAt: member.updatedAt }),
      REVOKED_TEXT,
      (problem) =>
        isConnectionProblem(problem) ? (
          <>
            Не удалось закрыть доступ: пропала связь. Доступ <b>не</b> закрыт. Повторите — это важно сделать до
            того, как человек уйдёт.
          </>
        ) : (
          `Доступ не закрыт: ${problem.detail ?? problem.title}`
        ),
    )

  const suspend = (): Promise<void> =>
    run(
      () => suspendMember({ memberId: member.memberId, expectedUpdatedAt: member.updatedAt }),
      "Доступ приостановлен: войти в кабинет салона сотрудник не сможет, пока вы его не вернёте.",
      (problem) =>
        isConnectionProblem(problem)
          ? "Не удалось приостановить: пропала связь. Доступ не приостановлен. Повторите."
          : `Доступ не приостановлен: ${problem.detail ?? problem.title}`,
    )

  const restore = (): Promise<void> =>
    run(
      () => restoreMember({ memberId: member.memberId, expectedUpdatedAt: member.updatedAt }),
      "Доступ возвращён: сотрудник снова может войти.",
      (problem) =>
        isConnectionProblem(problem)
          ? "Не удалось вернуть доступ: пропала связь. Повторите."
          : `Доступ не возвращён: ${problem.detail ?? problem.title}`,
    )

  // Окно правки — вместо карточки, а не поверх: два окна разом делят фокус и Escape.
  if (editing) {
    return (
      <EditAccessDialog
        member={member}
        branches={props.branches}
        onClose={() => setEditing(false)}
        onSaved={(next) => {
          changed(next)
          setEditing(false)
          setOutcome({ tone: "ok", text: "Роль и филиалы сохранены." })
        }}
      />
    )
  }

  return (
    <JournalDialog title={`Сотрудник · ${member.displayName}`} onClose={props.onClose}>
      <div className={styles.body}>
        {badge === null ? null : (
          <div>
            <Badge tone={badge.tone}>{badge.label}</Badge>
          </div>
        )}

        <dl className={styles.facts}>
          <div>
            <dt>Роль:</dt>
            <dd>{roleWords(member)}</dd>
          </div>
          <div>
            <dt>Филиалы:</dt>
            <dd>{branchesWords(member, props.branches)}</dd>
          </div>
          <div>
            <dt>Второй фактор:</dt>
            <dd>{mfaWords(member)}</dd>
          </div>
          {removed ? (
            <div>
              <dt>Доступ:</dt>
              <dd>{removedWords(member, props.timeZone)}</dd>
            </div>
          ) : props.canManage ? (
            <div>
              <dt>Активные входы:</dt>
              <dd>
                {sessions.data !== null
                  ? sessionsWords(sessions.data, props.timeZone)
                  : sessions.problem !== null
                    ? "не загрузились"
                    : "загружаем…"}
              </dd>
            </div>
          ) : null}
        </dl>

        {removed ? (
          <p className={styles.note}>
            Человек не удаляется: записи и история действий сотрудника остаются — иначе рвётся история «кто менял
            запись год назад».
          </p>
        ) : confirm === null ? (
          <div className={styles.actions}>
            <Button size="sm" variant="danger" deniedReason={denied} onClick={() => setConfirm("revoke")}>
              Закрыть доступ
            </Button>
            {member.status === "suspended" ? (
              <Button size="sm" deniedReason={denied} loading={busy} onClick={() => void restore()}>
                Вернуть доступ
              </Button>
            ) : (
              <Button size="sm" deniedReason={denied} onClick={() => setConfirm("suspend")}>
                Приостановить · отпуск, разбирательство
              </Button>
            )}
            <Button size="sm" deniedReason={denied} onClick={() => setEditing(true)}>
              Изменить роль и филиалы
            </Button>
          </div>
        ) : (
          <div className={styles.confirm}>
            <div className={styles.confirmTitle}>
              {confirm === "revoke" ? "Закрыть доступ насовсем?" : "Приостановить доступ?"}
            </div>
            <div>
              {confirm === "revoke"
                ? "Все сеансы сотрудника в салоне завершатся сразу, включая открытые на телефоне. Вернуть человека можно будет только новым приглашением."
                : "Войти в кабинет салона сотрудник не сможет, пока вы не вернёте доступ. Записи и график не меняются."}
            </div>
            <div className={styles.confirmButtons}>
              <Button size="sm" onClick={() => setConfirm(null)}>
                Отмена
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={busy}
                onClick={() => void (confirm === "revoke" ? revoke() : suspend())}
              >
                {confirm === "revoke" ? "Закрыть доступ" : "Приостановить"}
              </Button>
            </div>
          </div>
        )}

        {outcome === null ? null : (
          <div className={outcome.tone === "ok" ? styles.outcome : styles.problem} role={outcome.tone === "ok" ? "status" : "alert"}>
            {outcome.text}
          </div>
        )}
      </div>
    </JournalDialog>
  )
}

/** «Изменить роль и филиалы»: те же правила, что у приглашения (роль не выше своей). */
function EditAccessDialog(props: {
  readonly member: CabinetMemberView
  readonly branches: readonly CabinetBranchAccess[]
  readonly onClose: () => void
  readonly onSaved: (member: CabinetMemberView) => void
}): ReactNode {
  const roles = useResource((signal) => listRoles(signal), "cabinet-roles")
  const roleItems: readonly CabinetRoleOption[] = roles.data?.items ?? []
  const [roleId, setRoleId] = useState(props.member.roleId)
  const [allBranches, setAllBranches] = useState(props.member.branchIds.length === 0)
  const [branchIds, setBranchIds] = useState<readonly string[]>(props.member.branchIds)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const noBranch = !allBranches && branchIds.length === 0
  const save = async (): Promise<void> => {
    if (noBranch) {
      setProblem("Отметьте хотя бы один филиал — без филиала сотрудник не увидит ни одной записи")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      props.onSaved(
        await updateMemberAccess({
          memberId: props.member.memberId,
          roleId: roleId === props.member.roleId ? undefined : roleId,
          branchIds: allBranches ? [] : [...branchIds],
          expectedUpdatedAt: props.member.updatedAt,
        }),
      )
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(
        isConnectionProblem(error)
          ? "Не сохранилось: пропала связь. Роль и филиалы прежние. Повторите."
          : (error.detail ?? error.title),
      )
    } finally {
      setBusy(false)
    }
  }

  const selected = roleItems.find((role) => role.id === roleId)

  return (
    <JournalDialog title="Роль и филиалы" onClose={props.onClose}>
      <div className={styles.body}>
        <SelectField
          label="Роль"
          value={roleId}
          hint={selected !== undefined && !selected.grantable ? (selected.notGrantableReason ?? undefined) : undefined}
          options={
            roleItems.length === 0
              ? [{ value: props.member.roleId, label: roles.loading ? "Загружаем роли…" : props.member.roleName }]
              : roleItems.map((role) => ({
                  value: role.id,
                  label: role.grantable ? roleOptionLabel(role) : `${role.name} — выдаёт владелец`,
                }))
          }
          onChange={(event) => setRoleId(event.target.value)}
        />
        <fieldset className={styles.branches}>
          <legend className={styles.legend}>Филиалы</legend>
          <Checkbox checked={allBranches} onChange={setAllBranches}>
            Все филиалы, включая будущие
          </Checkbox>
          {allBranches
            ? null
            : props.branches.map((branch) => (
                <Checkbox
                  key={branch.id}
                  checked={branchIds.includes(branch.id)}
                  onChange={(checked) =>
                    setBranchIds(checked ? [...branchIds, branch.id] : branchIds.filter((id) => id !== branch.id))
                  }
                >
                  {branch.name}
                </Checkbox>
              ))}
        </fieldset>
        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.confirmButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
